import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, IconButton, TextField, Avatar, Badge, List,
  ListItemAvatar, ListItemText, ListItemButton, Divider, useTheme, alpha,
  Chip, Tooltip, InputAdornment, Fade, Menu, MenuItem, ListItemIcon,
  CircularProgress, Popover, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Switch, FormControlLabel, Tabs, Tab, Checkbox,
} from '@mui/material';
import {
  Send as SendIcon,
  EmojiEmotions as EmojiIcon, AttachFile as AttachIcon,
  Reply as ReplyIcon, Edit as EditIcon, Delete as DeleteIcon,
  PushPin as PinIcon, Search as SearchIcon,
  InsertDriveFile as FileIcon, DoneAll as ReadIcon,
  MoreVert as MoreIcon, Tag as TagIcon, Lock as LockIcon,
  Add as AddIcon, Close as CloseIcon, FormatBold as BoldIcon,
  FormatItalic as ItalicIcon, FormatStrikethrough as StrikeIcon,
  Code as CodeIcon, FormatListBulleted as ListIcon,
  Poll as PollIcon, PriorityHigh as PriorityIcon,
  Forum as ThreadIcon, Mic as MicIcon, Stop as StopIcon,
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../../stores/authStore';
import { useThemeStore } from '../../stores/themeStore';

// ═══════════════════════════════ Types ═══════════════════════════════

interface ChatChannel {
  id: number;
  name: string;
  description?: string;
  type: 'public' | 'private' | 'announcement';
  isPrivate: boolean;
  isDefault: boolean;
  createdBy: string;
  createdAt: string;
}

interface ChatMessage {
  id: number;
  content: string;
  messageType: string;
  createdAt: string;
  isEdited?: boolean;
  isDeleted?: boolean;
  isPinned?: boolean;
  isPriority?: boolean;
  formatting?: string;
  pinnedBy?: string;
  pinnedAt?: string;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  replyToId?: number;
  channelId?: number;
  roomId?: number;
  threadId?: number;
  replyCount?: number;
  lastReplyAt?: string;
  replyTo?: {
    id: number;
    content: string;
    sender: { id: string; email: string; firstName?: string; lastName?: string };
  };
  reactions?: { emoji: string; count: number; users: { id: string; email: string }[] }[];
  readBy?: { userId: string; email: string; readAt: string }[];
  sender: { id: string; email: string; firstName?: string; lastName?: string };
  poll?: ChatPoll;
}

interface OnlineUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  status: 'available' | 'busy' | 'away' | 'dnd' | 'brb' | 'offline' | 'invisible';
  statusMessage?: string;
  statusEmoji?: string;
  lastSeen: string;
  domain?: string;
}

interface DmRoom {
  id: number;
  otherUser: { id: string; email: string; firstName?: string; lastName?: string; domain?: string; status?: string };
  lastMessage?: { content: string; messageType: string; createdAt: string; senderId: string };
  unreadCount: number;
}

interface ChatPoll {
  id: number;
  question: string;
  options: string[];
  multiSelect: boolean;
  votes: { optionIndex: number; userId: string; count: number }[];
  totalVotes: number;
}

// ═══════════════════════════════ Helpers ═══════════════════════════════

const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '😮', '😢'];

const EMOJI_LIST = ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','😊','😇','🥰','😍','🤩','😘','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','👋','🤚','✋','👌','✌️','🤞','🤟','🤘','🤙','👍','👎','✊','👊','👏','🙌','🤝','🙏','❤️','🧡','💛','💚','💙','💜','🖤','🤍','💔','💕','💯','✅','❌','🔥','⭐','✨','💫','🎉','🎊','🎁','🏆','⚡','💡','📌','🔔','🎵','☕','🍕','🍔','🍻'];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'available': case 'online': return '#10b981';
    case 'busy': return '#ef4444';
    case 'away': return '#f59e0b';
    case 'dnd': return '#dc2626';
    case 'brb': return '#8b5cf6';
    case 'invisible': case 'offline': return '#6b7280';
    default: return '#6b7280';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'available': return 'Available';
    case 'busy': return 'Busy';
    case 'away': return 'Away';
    case 'dnd': return 'Do not disturb';
    case 'brb': return 'Be right back';
    case 'invisible': return 'Appear offline';
    case 'offline': return 'Offline';
    default: return 'Unknown';
  }
};

const getAvatarGradient = (email: string) => {
  const colors = [
    'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
    'linear-gradient(135deg, #059669 0%, #047857 100%)',
    'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
    'linear-gradient(135deg, #db2777 0%, #be185d 100%)',
    'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
    'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
  ];
  return colors[(email || '').charCodeAt(0) % colors.length];
};

const formatTime = (dateStr: string) => new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
};

const formatFileSize = (bytes: number) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const getUserDisplayName = (sender: { firstName?: string; lastName?: string; email?: string }) => {
  if (sender?.firstName || sender?.lastName) return `${sender.firstName || ''} ${sender.lastName || ''}`.trim();
  return sender?.email?.split('@')[0] || 'Unknown';
};

const getInitials = (sender: { firstName?: string; lastName?: string; email?: string }) => {
  if (sender?.firstName && sender?.lastName) return `${sender.firstName[0]}${sender.lastName[0]}`.toUpperCase();
  if (sender?.firstName) return sender.firstName.substring(0, 2).toUpperCase();
  return sender?.email?.substring(0, 2).toUpperCase() || '??';
};

const isSameDay = (d1: string, d2: string) => new Date(d1).toDateString() === new Date(d2).toDateString();

const isSameMinuteAndSender = (m1: ChatMessage, m2: ChatMessage) => {
  if (m1.sender.id !== m2.sender.id) return false;
  const t1 = new Date(m1.createdAt).getTime();
  const t2 = new Date(m2.createdAt).getTime();
  return Math.abs(t1 - t2) < 60000;
};

// ═══════════════════════════════ Component ═══════════════════════════════

type SidebarTab = 'channels' | 'dms';
type MainView = 'channel' | 'dm';

const TeamChatPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { accentColor } = useThemeStore();
  const { token, user } = useAuthStore();

  const accentColors: Record<string, { main: string; light: string; dark: string }> = {
    blue: { main: '#1a73e8', light: '#4285f4', dark: '#1557b0' },
    green: { main: '#34a853', light: '#5bb974', dark: '#1e8e3e' },
    teal: { main: '#009688', light: '#4db6ac', dark: '#00796b' },
    purple: { main: '#7c3aed', light: '#a78bfa', dark: '#6d28d9' },
    orange: { main: '#ea580c', light: '#fb923c', dark: '#c2410c' },
    red: { main: '#ef4444', light: '#f87171', dark: '#dc2626' },
    pink: { main: '#ec4899', light: '#f472b6', dark: '#db2777' },
    yellow: { main: '#eab308', light: '#facc15', dark: '#ca8a04' },
    gray: { main: '#6b7280', light: '#9ca3af', dark: '#4b5563' },
  };
  const accent = accentColors[accentColor] || accentColors.blue;

  // ───────── Sidebar ─────────
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('dms');
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [activeChannelId, setActiveChannelId] = useState<number | null>(null);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [selectedChannelIds, setSelectedChannelIds] = useState<Set<number>>(new Set());
  const [dmRooms, setDmRooms] = useState<DmRoom[]>([]);
  const [activeDmRoom, setActiveDmRoom] = useState<DmRoom | null>(null);
  const [mainView, setMainView] = useState<MainView>('channel');

  // Refs to access latest values in Socket.io listeners without causing reconnections
  const activeDmRoomRef = useRef<DmRoom | null>(null);
  const mainViewRef = useRef<MainView>('channel');
  
  // Keep refs in sync with state
  useEffect(() => { activeDmRoomRef.current = activeDmRoom; }, [activeDmRoom]);
  useEffect(() => { mainViewRef.current = mainView; }, [mainView]);

  // ───────── Messages ─────────
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [dmMessages, setDmMessages] = useState<ChatMessage[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [message, setMessage] = useState('');
  const [connected, setConnected] = useState(false);

  // ───────── Typing ─────────
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [dmTypingUsers, setDmTypingUsers] = useState<Array<{ roomId: number; email: string }>>([]);

  // ───────── Thread ─────────
  const [threadParent, setThreadParent] = useState<ChatMessage | null>(null);
  const [threadReplies, setThreadReplies] = useState<ChatMessage[]>([]);
  const [threadMessage, setThreadMessage] = useState('');
  const [threadOpen, setThreadOpen] = useState(false);

  // ───────── Reply / edit ─────────
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);

  // ───────── Formatting ─────────
  const [showFormatBar, setShowFormatBar] = useState(false);
  const [isPriority, setIsPriority] = useState(false);

  // ───────── Search ─────────
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ChatMessage[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // ───────── Pinned ─────────
  const [pinnedMessages, setPinnedMessages] = useState<ChatMessage[]>([]);
  const [showPinned, setShowPinned] = useState(false);

  // ───────── Emoji ─────────
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);
  const [emojiTarget, setEmojiTarget] = useState<'input' | number>('input');

  // ───────── Context menu ─────────
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; message: ChatMessage } | null>(null);
  const [hoverMsgId, setHoverMsgId] = useState<number | null>(null);

  // ───────── File upload ─────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const dragCounterRef = useRef(0);

  // ───────── Pasted image ─────────
  const [pastedImageFile, setPastedImageFile] = useState<File | null>(null);
  const [pastedImagePreviewUrl, setPastedImagePreviewUrl] = useState<string | null>(null);

  // ───────── Voice ─────────
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // ───────── Poll ─────────
  const [pollDialogOpen, setPollDialogOpen] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);

  // ───────── User status ─────────
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);
  const [currentUserStatus, setCurrentUserStatus] = useState<'available' | 'busy' | 'away' | 'dnd' | 'brb' | 'invisible'>('available');
  const [statusMessage, setStatusMessage] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [pollMultiSelect, setPollMultiSelect] = useState(false);

  // ───────── Group/Channel dialog ─────────
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelDesc, setNewChannelDesc] = useState('');
  const [newChannelPrivate, setNewChannelPrivate] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  
  // ───────── Members dialog ─────────
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [channelMembers, setChannelMembers] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // ───────── Refs ─────────
  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const threadEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileUploadRef = useRef<((file: File) => Promise<void>) | null>(null);

  const currentUserId = user?.id || '';
  const currentUserEmail = user?.email || '';
  const apiUrl = import.meta.env.VITE_API_URL || '/api';

  // ═══════════════ Active channel ═══════════════
  const activeChannel = useMemo(() => channels.find(c => c.id === activeChannelId), [channels, activeChannelId]);

  // ═══════════════ Scroll ═══════════════
  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  const scrollThreadToBottom = useCallback((smooth = true) => {
    threadEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // ═══════════════ API helpers ═══════════════
  const authHeaders = useMemo(() => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const fetchChannels = useCallback(async () => {
    try {
      console.log('[Chat] Fetching channels from:', `${apiUrl}/chat/channels`);
      const res = await fetch(`${apiUrl}/chat/channels`, { headers: authHeaders });
      console.log('[Chat] fetchChannels response:', res.status, res.statusText);
      
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.warn('[Chat] fetchChannels returned', res.status, text);
        return;
      }
      const json = await res.json();
      console.log('[Chat] fetchChannels JSON:', json);
      
      // Handle double-nested response: {success: true, data: {success: true, data: [array]}}
      const data = json.data?.data || json.data || json;
      console.log('[Chat] Extracted data:', data);
      
      if (Array.isArray(data)) {
        console.log('[Chat] Setting channels:', data.length, 'channels');
        setChannels(data);
        setActiveChannelId(prev => {
          if (!prev && data.length > 0) {
            const def = data.find((c: ChatChannel) => c.isDefault) || data[0];
            console.log('[Chat] Auto-selecting channel:', def.id, def.name);
            return def.id;
          }
          return prev;
        });
      } else {
        console.error('[Chat] fetchChannels unexpected shape:', json);
        console.error('[Chat] Expected array but got:', data);
      }
    } catch (err) {
      console.warn('[Chat] fetchChannels error:', err);
    }
  }, [apiUrl, authHeaders]);

  const fetchChannelMembers = useCallback(async (channelId: number) => {
    setLoadingMembers(true);
    try {
      console.log('[Chat] Fetching members for channel:', channelId);
      const res = await fetch(`${apiUrl}/chat/channels/${channelId}/members`, { headers: authHeaders });
      const json = await res.json();
      console.log('[Chat] Members response:', json);
      // Handle double-nested response: {success: true, data: {success: true, data: [array]}}
      const data = json.data?.data || json.data || json;
      console.log('[Chat] Extracted members data:', data);
      if (Array.isArray(data)) {
        setChannelMembers(data);
        setMembersDialogOpen(true);
      } else {
        console.warn('[Chat] Members data is not an array:', data);
      }
    } catch (err) {
      console.error('[Chat] Error fetching members:', err);
    } finally {
      setLoadingMembers(false);
    }
  }, [apiUrl, authHeaders]);

  const fetchChannelMessages = useCallback(async (channelId: number) => {
    try {
      const res = await fetch(`${apiUrl}/chat/messages?channelId=${channelId}`, { headers: authHeaders });
      const json = await res.json();
      // Handle double-nested response: {success: true, data: {success: true, data: [array]}}
      const data = json.data?.data || json.data || json;
      if (Array.isArray(data)) {
        // Accumulate messages instead of replacing - merge with existing
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMessages = data.filter((msg: ChatMessage) => !existingIds.has(msg.id));
          // Remove old messages for this channel, add fetched ones
          const otherChannelMessages = prev.filter(m => m.channelId !== channelId);
          return [...otherChannelMessages, ...data];
        });
        setTimeout(() => scrollToBottom(false), 100);
      }
    } catch {}
  }, [apiUrl, authHeaders, scrollToBottom]);

  const fetchDmRooms = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/chat/dm/rooms`, { headers: authHeaders });
      const json = await res.json();
      // Handle double-nested response
      const data = json.data?.data || json.data || json;
      if (Array.isArray(data)) setDmRooms(data);
    } catch {}
  }, [apiUrl, authHeaders]);

  const fetchDmMessages = useCallback(async (roomId: number) => {
    try {
      const res = await fetch(`${apiUrl}/chat/dm/${roomId}/messages`, { headers: authHeaders });
      const json = await res.json();
      // Handle double-nested response
      const data = json.data?.data || json.data || json;
      if (Array.isArray(data)) {
        setDmMessages(data);
        setTimeout(() => scrollToBottom(false), 100);
      }
    } catch {}
  }, [apiUrl, authHeaders, scrollToBottom]);

  const fetchPinnedMessages = useCallback(async () => {
    try {
      const url = activeChannelId ? `${apiUrl}/chat/pinned?channelId=${activeChannelId}` : `${apiUrl}/chat/pinned`;
      const res = await fetch(url, { headers: authHeaders });
      const json = await res.json();
      // Handle double-nested response
      const data = json.data?.data || json.data || json;
      if (Array.isArray(data)) setPinnedMessages(data);
    } catch {}
  }, [apiUrl, authHeaders, activeChannelId]);

  const fetchThreadReplies = useCallback(async (threadId: number) => {
    try {
      const res = await fetch(`${apiUrl}/chat/threads/${threadId}`, { headers: authHeaders });
      const json = await res.json();
      // Handle double-nested response
      const data = json.data?.data || json.data || json;
      if (Array.isArray(data)) {
        setThreadReplies(data);
        setTimeout(() => scrollThreadToBottom(false), 100);
      }
    } catch {}
  }, [apiUrl, authHeaders, scrollThreadToBottom]);

  // ═══════════════ Socket Connection ═══════════════
  const connectSocket = useCallback(() => {
    if (!token || socketRef.current?.connected) return;
    // Use window.location.origin to connect to same server where frontend is hosted
    // Nginx will proxy /socket.io/ to the backend
    const socketUrl = window.location.origin;
    console.log('[Chat Socket] Connecting to:', socketUrl);
    socketRef.current = io(socketUrl, { 
      path: '/socket.io',
      auth: { token }, 
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('connect', () => {
      console.log('[Chat Socket] Connected!');
      setConnected(true);
    });

    socketRef.current.on('connected', () => {
      console.log('[Chat Socket] Received connected event');
      setConnected(true);
      fetchChannels();
      fetchDmRooms();
    });

    socketRef.current.on('error', () => setConnected(false));
    socketRef.current.on('connect_error', (err) => {
      console.error('[Chat Socket] Connection error:', err);
      setConnected(false);
    });
    socketRef.current.on('disconnect', (reason) => {
      console.log('[Chat Socket] Disconnected:', reason);
      setConnected(false);
    });

    // Online users
    socketRef.current.on('onlineUsers', (users: OnlineUser[]) => setOnlineUsers(users));
    socketRef.current.on('userOnline', (data: { userId: string; email: string }) => {
      setOnlineUsers(prev => {
        if (prev.find(u => u.id === data.userId)) return prev.map(u => u.id === data.userId ? { ...u, status: 'available' as const } : u);
        return [...prev, { id: data.userId, email: data.email, status: 'available' as const, lastSeen: new Date().toISOString() }];
      });
    });
    socketRef.current.on('userOffline', (data: { userId: string }) => {
      setOnlineUsers(prev => prev.map(u => u.id === data.userId ? { ...u, status: 'offline' as const } : u));
    });
    socketRef.current.on('userStatusChange', (data: { userId: string; status: string; statusMessage?: string; statusEmoji?: string }) => {
      setOnlineUsers(prev => prev.map(u => u.id === data.userId ? { ...u, status: data.status as any, statusMessage: data.statusMessage, statusEmoji: data.statusEmoji } : u));
    });

    // Channel created by anyone
    socketRef.current.on('channelCreated', (ch: ChatChannel) => {
      setChannels(prev => prev.find(c => c.id === ch.id) ? prev : [...prev, ch]);
    });

    // Group messages
    socketRef.current.on('newMessage', (msg: ChatMessage) => {
      if (msg.threadId) return; // thread replies handled separately
      setMessages(prev => {
        // Only add if not duplicate
        if (prev.find(m => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      setTypingUsers(prev => prev.filter(e => e !== msg.sender.email));
      setTimeout(scrollToBottom, 100);
    });
    socketRef.current.on('newChannelMessage', () => {
      // Already handled by newMessage
    });
    socketRef.current.on('threadReply', (data: { threadId: number; message: ChatMessage }) => {
      // Update reply count on parent
      setMessages(prev => prev.map(m => m.id === data.threadId ? { ...m, replyCount: (m.replyCount || 0) + 1, lastReplyAt: data.message.createdAt } : m));
      // If viewing this thread, add reply
      if (threadParent?.id === data.threadId) {
        setThreadReplies(prev => [...prev, data.message]);
        setTimeout(scrollThreadToBottom, 100);
      }
    });
    socketRef.current.on('messageEdited', (msg: ChatMessage) => {
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, ...msg } : m));
      setThreadReplies(prev => prev.map(m => m.id === msg.id ? { ...m, ...msg } : m));
    });
    socketRef.current.on('messageDeleted', (data: { id: number }) => {
      setMessages(prev => prev.map(m => m.id === data.id ? { ...m, isDeleted: true, content: '' } : m));
      setThreadReplies(prev => prev.map(m => m.id === data.id ? { ...m, isDeleted: true, content: '' } : m));
    });
    socketRef.current.on('reactionUpdate', (data: { messageId: number; reactions: any[] }) => {
      setMessages(prev => prev.map(m => m.id === data.messageId ? { ...m, reactions: data.reactions } : m));
      setThreadReplies(prev => prev.map(m => m.id === data.messageId ? { ...m, reactions: data.reactions } : m));
    });
    socketRef.current.on('messagePinned', (msg: ChatMessage) => {
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, isPinned: true } : m));
    });
    socketRef.current.on('messageUnpinned', (data: { id: number }) => {
      setMessages(prev => prev.map(m => m.id === data.id ? { ...m, isPinned: false } : m));
      setPinnedMessages(prev => prev.filter(m => m.id !== data.id));
    });

    // Typing
    socketRef.current.on('userTyping', (data: { email: string; channelId?: number }) => {
      setTypingUsers(prev => prev.includes(data.email) ? prev : [...prev, data.email]);
      setTimeout(() => setTypingUsers(prev => prev.filter(e => e !== data.email)), 4000);
    });
    socketRef.current.on('userStopTyping', (data: { email: string }) => {
      setTypingUsers(prev => prev.filter(e => e !== data.email));
    });

    // DM events
    socketRef.current.on('newDmMessage', (msg: ChatMessage) => {
      console.log('[Chat] Received newDmMessage:', msg, 'roomId:', msg.roomId, 'active room:', activeDmRoomRef.current?.id);
      console.log('[Chat] mainView:', mainViewRef.current, 'activeDmRoom exists:', !!activeDmRoomRef.current);
      // Only add message if it belongs to the currently active DM room
      if (mainViewRef.current === 'dm' && activeDmRoomRef.current && msg.roomId === activeDmRoomRef.current.id) {
        setDmMessages(prev => {
          // Prevent duplicates
          if (prev.some(m => m.id === msg.id)) return prev;
          console.log('[Chat] Adding message to current DM');
          return [...prev, msg];
        });
        setTimeout(scrollToBottom, 100);
      } else {
        console.log('[Chat] NOT adding message - mismatch or not in DM view');
      }
      // Always refresh rooms list to update last message preview and unread count
      fetchDmRooms();
    });
    socketRef.current.on('dmRead', () => {});
    socketRef.current.on('dmRoomCreated', (data: { room: DmRoom }) => {
      console.log('🔔 [Chat Socket] dmRoomCreated - Someone started a DM with you:', data.room);
      console.log('🔔 [Chat Socket] Automatically joining room dm:' + data.room.id);
      // Refresh DM rooms list when someone starts a conversation with you
      fetchDmRooms();
    });
    socketRef.current.on('dmUserTyping', (data: { roomId: number; email: string }) => {
      console.log('[Chat Socket] dmUserTyping:', data);
      console.log('[Chat Socket] Current activeDmRoom:', activeDmRoomRef.current);
      setDmTypingUsers(prev => {
        // Check if already exists
        if (prev.some(t => t.roomId === data.roomId && t.email === data.email)) return prev;
        return [...prev, { roomId: data.roomId, email: data.email }];
      });
      setTimeout(() => {
        setDmTypingUsers(prev => prev.filter(t => !(t.roomId === data.roomId && t.email === data.email)));
      }, 4000);
    });
    socketRef.current.on('dmUserStopTyping', (data: { roomId: number; email: string }) => {
      console.log('[Chat Socket] dmUserStopTyping:', data);
      setDmTypingUsers(prev => prev.filter(t => !(t.roomId === data.roomId && t.email === data.email)));
    });

    // Poll updates
    socketRef.current.on('pollUpdate', (data: { pollId: number; results: any }) => {
      setMessages(prev => prev.map(m => m.poll?.id === data.pollId ? { ...m, poll: { ...m.poll!, ...data.results } } : m));
    });

    // Read receipts
    socketRef.current.on('messageRead', (data: { messageId: number; userId: string; email: string }) => {
      setMessages(prev => prev.map(m => m.id === data.messageId ? { ...m, readBy: [...(m.readBy || []), { userId: data.userId, email: data.email, readAt: new Date().toISOString() }] } : m));
    });
  }, [token, apiUrl, fetchChannels, fetchDmRooms, scrollToBottom, scrollThreadToBottom, threadParent]);

  // ═══════════════ Effects ═══════════════
  useEffect(() => {
    connectSocket();
    return () => { socketRef.current?.disconnect(); };
  }, [connectSocket]);

  // Fetch channels independently of socket (fallback if socket 'connected' never fires)
  useEffect(() => {
    if (token) {
      fetchChannels();
      fetchDmRooms();
    }
  }, [token, fetchChannels, fetchDmRooms]);

  useEffect(() => {
    if (activeChannelId && connected) {
      fetchChannelMessages(activeChannelId);
      socketRef.current?.emit('joinChannel', { channelId: activeChannelId });
    }
  }, [activeChannelId, connected, fetchChannelMessages]);

  // ═══════════════ Actions ═══════════════

  const handleSendMessage = useCallback(async () => {
    // If there's a pasted image, upload it via the ref (defined later, but ref is always current)
    if (pastedImageFile) {
      if (fileUploadRef.current) await fileUploadRef.current(pastedImageFile);
      setPastedImageFile(null);
      if (pastedImagePreviewUrl) { URL.revokeObjectURL(pastedImagePreviewUrl); setPastedImagePreviewUrl(null); }
      return;
    }

    const content = message.trim();
    if (!content || !socketRef.current) return;

    if (editingMessage) {
      socketRef.current.emit('editMessage', { messageId: editingMessage.id, content });
      setEditingMessage(null);
      setMessage('');
      return;
    }

    if (mainView === 'dm' && activeDmRoom) {
      console.log('[Chat] Sending DM:', { roomId: activeDmRoom.id, content, activeDmRoom });
      socketRef.current.emit('sendDm', { roomId: activeDmRoom.id, content, replyToId: replyTo?.id });
    } else {
      socketRef.current.emit('sendMessage', {
        content,
        channelId: activeChannelId,
        replyToId: replyTo?.id,
        isPriority,
      });
    }
    setMessage('');
    setReplyTo(null);
    setIsPriority(false);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    socketRef.current?.emit('stopTyping', { channelId: activeChannelId });
  }, [message, pastedImageFile, pastedImagePreviewUrl, editingMessage, mainView, activeDmRoom, activeChannelId, replyTo, isPriority]);

  const handleSendThreadReply = useCallback(() => {
    const content = threadMessage.trim();
    if (!content || !socketRef.current || !threadParent) return;
    socketRef.current.emit('sendMessage', { content, channelId: activeChannelId, threadId: threadParent.id });
    setThreadMessage('');
  }, [threadMessage, threadParent, activeChannelId]);

  const handleTyping = useCallback(() => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    if (mainView === 'dm' && activeDmRoom) {
      console.log('[Chat] Emitting dmTyping for room:', activeDmRoom.id);
      socketRef.current?.emit('dmTyping', { roomId: activeDmRoom.id });
      typingTimeoutRef.current = setTimeout(() => {
        console.log('[Chat] Emitting dmStopTyping for room:', activeDmRoom.id);
        socketRef.current?.emit('dmStopTyping', { roomId: activeDmRoom.id });
      }, 2000);
    } else {
      socketRef.current?.emit('typing', { channelId: activeChannelId });
      typingTimeoutRef.current = setTimeout(() => socketRef.current?.emit('stopTyping', { channelId: activeChannelId }), 2000);
    }
  }, [mainView, activeDmRoom, activeChannelId]);

  const handleReaction = useCallback((messageId: number, emoji: string) => {
    socketRef.current?.emit('toggleReaction', { messageId, emoji });
  }, []);

  const handlePin = useCallback((messageId: number) => {
    socketRef.current?.emit('pinMessage', { messageId });
  }, []);

  const handleUnpin = useCallback((messageId: number) => {
    socketRef.current?.emit('unpinMessage', { messageId });
  }, []);

  const handleDeleteMsg = useCallback((messageId: number) => {
    socketRef.current?.emit('deleteMessage', { messageId });
  }, []);

  const handleMarkRead = useCallback((messageId: number) => {
    socketRef.current?.emit('markRead', { messageId });
  // eslint-disable-next-line
  }, []);

  const openThread = useCallback((msg: ChatMessage) => {
    setThreadParent(msg);
    setThreadOpen(true);
    fetchThreadReplies(msg.id);
  }, [fetchThreadReplies]);

  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const url = activeChannelId
        ? `${apiUrl}/chat/search?q=${encodeURIComponent(query)}&channelId=${activeChannelId}`
        : `${apiUrl}/chat/search?q=${encodeURIComponent(query)}`;
      const res = await fetch(url, { headers: authHeaders });
      const json = await res.json();
      setSearchResults(json.data || json || []);
    } catch {} finally { setSearchLoading(false); }
  }, [apiUrl, authHeaders, activeChannelId]);

  // ─── File upload ───
  const handleFileUpload = useCallback(async (file: File) => {
    if (!file) return;
    
    // File size validation (25MB limit matching email attachment limits)
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      alert(`File size exceeds the limit of 25MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`);
      return;
    }
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (activeChannelId) formData.append('channelId', String(activeChannelId));
      const endpoint = mainView === 'dm' && activeDmRoom ? `${apiUrl}/chat/dm/upload` : `${apiUrl}/chat/upload`;
      if (mainView === 'dm' && activeDmRoom) formData.append('roomId', String(activeDmRoom.id));
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
    } catch (err) {
      console.error('[Chat] Upload error:', err);
      alert('Failed to upload file. Please try again.');
    } finally { 
      setUploadingFile(false); 
    }
  }, [apiUrl, token, activeChannelId, mainView, activeDmRoom]);

  // Keep fileUploadRef always pointing to the latest handleFileUpload
  useEffect(() => { fileUploadRef.current = handleFileUpload; }, [handleFileUpload]);

  // ─── Drag and drop file upload ───
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDraggingFile(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDraggingFile(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Upload the first file
      handleFileUpload(files[0]);
    }
  }, [handleFileUpload]);

  // ─── Paste image ───
  // Shared core: extracts an image from a ClipboardEvent's data and stages it for sending.
  // Returns true if an image was handled.
  const ingestClipboardImage = useCallback((cd: DataTransfer | null): boolean => {
    if (!cd) return false;
    // Prefer items API (covers screenshots), fall back to files.
    let file: File | null = null;
    if (cd.items && cd.items.length) {
      const imageItem = Array.from(cd.items).find(it => it.kind === 'file' && it.type.startsWith('image/'));
      if (imageItem) file = imageItem.getAsFile();
    }
    if (!file && cd.files && cd.files.length) {
      const f = Array.from(cd.files).find(f => f.type.startsWith('image/'));
      if (f) file = f;
    }
    if (!file) return false;
    const ext = (file.type.split('/')[1] || 'png').replace(/[^a-z0-9]/gi, '') || 'png';
    const renamedFile = new File([file], `paste-${Date.now()}.${ext}`, { type: file.type || 'image/png' });
    setPastedImageFile(renamedFile);
    setPastedImagePreviewUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(renamedFile);
    });
    return true;
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    if (ingestClipboardImage(e.clipboardData)) e.preventDefault();
  }, [ingestClipboardImage]);

  // Window-level fallback: lets users paste an image anywhere on the chat page,
  // not only when the textarea is focused (Slack/Discord-style UX).
  useEffect(() => {
    const onWindowPaste = (e: ClipboardEvent) => {
      // Skip if user is typing in another text input/area/contenteditable that should consume the paste.
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      const isEditable = !!t && (t.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA');
      // If editable target is NOT our composer, let the browser handle text paste normally.
      // We still try to extract images even from our own composer (handled by onPaste), but
      // if no React handler caught it (e.g. focus is on the page body), handle it here.
      if (isEditable) {
        // The React onPaste on the composer textarea will handle this; don't double-fire.
        const isComposer = !!t.closest('[data-composer-textarea="true"]');
        if (isComposer) return;
      }
      if (ingestClipboardImage(e.clipboardData)) {
        e.preventDefault();
      }
    };
    window.addEventListener('paste', onWindowPaste);
    return () => window.removeEventListener('paste', onWindowPaste);
  }, [ingestClipboardImage]);

  // ─── Voice message ───
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => audioChunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: 'audio/webm' });
        await handleFileUpload(file);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch {}
  }, [handleFileUpload]);

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  }, []);

  // ─── User status ───
  const handleStatusChange = useCallback((newStatus: typeof currentUserStatus, message?: string) => {
    setCurrentUserStatus(newStatus);
    if (message !== undefined) setStatusMessage(message);
    if (socketRef.current) {
      socketRef.current.emit('changeStatus', { status: newStatus, statusMessage: message || statusMessage });
    }
    setStatusMenuAnchor(null);
  }, [statusMessage]);

  // ─── User filtering ───
  const userDomain = useMemo(() => user?.email?.split('@')[1] || '', [user]);
  
  const filteredUsers = useMemo(() => {
    let users = onlineUsers.filter(u => u.id !== user?.id);
    // Filter by same domain
    users = users.filter(u => u.email?.split('@')[1] === userDomain);
    // Apply search filter
    if (userFilter.trim()) {
      const query = userFilter.toLowerCase();
      users = users.filter(u => 
        getUserDisplayName(u).toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query) ||
        u.statusMessage?.toLowerCase().includes(query)
      );
    }
    // Sort: available first, then by name
    return users.sort((a, b) => {
      if (a.status === 'available' && b.status !== 'available') return -1;
      if (a.status !== 'available' && b.status === 'available') return 1;
      return getUserDisplayName(a).localeCompare(getUserDisplayName(b));
    });
  }, [onlineUsers, user, userDomain, userFilter]);

  // Filter out users who already have DM conversations
  const teamUsersWithoutConversations = useMemo(() => {
    const dmUserIds = new Set(dmRooms.map(room => room.otherUser.id));
    return filteredUsers.filter(u => !dmUserIds.has(u.id));
  }, [filteredUsers, dmRooms]);

  // ─── Create channel ───
  const handleCreateChannel = useCallback(async () => {
    if (!newChannelName.trim()) return;
    try {
      const memberIdsArray = Array.from(selectedMembers);
      console.log('[Chat] Creating group:', { 
        name: newChannelName, 
        description: newChannelDesc, 
        isPrivate: newChannelPrivate,
        memberIds: memberIdsArray,
        memberCount: memberIdsArray.length,
      });
      
      const res = await fetch(`${apiUrl}/chat/channels`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ 
          name: newChannelName, 
          description: newChannelDesc, 
          isPrivate: newChannelPrivate,
          memberIds: memberIdsArray,
        }),
      });
      
      console.log('[Chat] Response status:', res.status, res.statusText);
      
      let json: any = {};
      try { json = await res.json(); } catch {}
      
      console.log('[Chat] Response body:', json);
      
      if (!res.ok) {
        console.error('[Chat] Create group failed:', res.status, json);
        alert(`Failed to create group: ${json.message || res.statusText}`);
        return;
      }
      const created = json.data || json;
      console.log('[Chat] Group created:', created);
      
      setChannelDialogOpen(false);
      setNewChannelName('');
      setNewChannelDesc('');
      setNewChannelPrivate(false);
      setSelectedMembers(new Set());
      setMemberSearchQuery('');
      
      // Optimistically add and switch to the new channel immediately
      if (created?.id) {
        setChannels(prev => [...prev.filter(c => c.id !== created.id), created]);
        setActiveChannelId(created.id);
        setMainView('channel');
      }
      fetchChannels(); // sync with server in background
    } catch (err) {
      console.error('[Chat] Create group error:', err);
      alert(`Error creating group: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [apiUrl, authHeaders, newChannelName, newChannelDesc, newChannelPrivate, selectedMembers, fetchChannels]);

  // ─── Delete channel ───
  const handleDeleteChannel = useCallback(async (channelId: number, channelName: string) => {
    if (!confirm(`Delete channel #${channelName}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${apiUrl}/chat/channels/${channelId}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        alert(`Failed to delete channel: ${json.message || res.statusText}`);
        return;
      }
      // Remove from local state
      setChannels(prev => prev.filter(c => c.id !== channelId));
      // If this was the active channel, clear selection
      if (activeChannelId === channelId) {
        setActiveChannelId(null);
      }
    } catch (err) {
      console.error('[Chat] Delete channel error:', err);
      alert(`Error deleting channel: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [apiUrl, authHeaders, activeChannelId]);

  // ─── Bulk delete channels ───
  const handleBulkDelete = useCallback(async () => {
    if (selectedChannelIds.size === 0) return;
    const count = selectedChannelIds.size;
    if (!confirm(`Delete ${count} channel${count > 1 ? 's' : ''}? This cannot be undone.`)) return;
    try {
      const deletePromises = Array.from(selectedChannelIds).map(id =>
        fetch(`${apiUrl}/chat/channels/${id}`, {
          method: 'DELETE',
          headers: authHeaders,
        })
      );
      await Promise.all(deletePromises);
      setChannels(prev => prev.filter(c => !selectedChannelIds.has(c.id)));
      if (activeChannelId && selectedChannelIds.has(activeChannelId)) {
        setActiveChannelId(null);
      }
      setSelectedChannelIds(new Set());
      setBulkDeleteMode(false);
    } catch (err) {
      console.error('[Chat] Bulk delete error:', err);
      alert(`Error deleting channels: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [selectedChannelIds, apiUrl, authHeaders, activeChannelId]);

  const toggleChannelSelection = useCallback((channelId: number) => {
    setSelectedChannelIds(prev => {
      const next = new Set(prev);
      if (next.has(channelId)) next.delete(channelId);
      else next.add(channelId);
      return next;
    });
  }, []);

  const selectAllChannels = useCallback(() => {
    const nonDefaultChannels = channels.filter(c => !c.isDefault);
    setSelectedChannelIds(new Set(nonDefaultChannels.map(c => c.id)));
  }, [channels]);

  // ─── Create poll ───
  const handleCreatePoll = useCallback(async () => {
    if (!pollQuestion.trim() || pollOptions.filter(o => o.trim()).length < 2) return;
    try {
      await fetch(`${apiUrl}/chat/polls`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          question: pollQuestion,
          options: pollOptions.filter(o => o.trim()),
          multiSelect: pollMultiSelect,
          channelId: activeChannelId,
        }),
      });
      setPollDialogOpen(false);
      setPollQuestion('');
      setPollOptions(['', '']);
      setPollMultiSelect(false);
    } catch {}
  }, [apiUrl, authHeaders, pollQuestion, pollOptions, pollMultiSelect, activeChannelId]);

  const handleVotePoll = useCallback(async (pollId: number, optionIndex: number) => {
    try {
      await fetch(`${apiUrl}/chat/polls/${pollId}/vote`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ optionIndex }),
      });
    } catch {}
  }, [apiUrl, authHeaders]);

  // ─── Open DM ───
  const handleOpenDm = useCallback(async (userId: string) => {
    if (!socketRef.current) {
      console.error('[Chat] Socket not connected when trying to open DM');
      return;
    }
    console.log('[Chat] Opening DM with user:', userId);
    console.log('[Chat] Socket connected:', socketRef.current.connected);
    console.log('[Chat] Socket ID:', socketRef.current.id);
    socketRef.current.emit('openDm', { otherUserId: userId }, (response: any) => {
      console.log('[Chat] openDm response:', response);
      if (response?.success) {
        setActiveDmRoom(response.data.room);
        setDmMessages(response.data.messages || []);
        console.log('[Chat] Set dmMessages to:', response.data.messages?.length || 0, 'messages');
        console.log('[Chat] Joined room dm:' + response.data.room.id);
        setMainView('dm');
        setSidebarTab('dms');
        fetchDmRooms();
        setTimeout(() => scrollToBottom(false), 100);
      } else {
        console.error('[Chat] Failed to open DM:', response);
      }
    });
  }, [fetchDmRooms, scrollToBottom]);

  // ═══════════════ Grouped messages ═══════════════
  const currentMessages = mainView === 'dm' 
    ? dmMessages 
    : messages.filter(msg => msg.channelId === activeChannelId);
  const currentTyping = mainView === 'dm' 
    ? dmTypingUsers
        .filter(t => activeDmRoom && t.roomId === activeDmRoom.id)
        .map(t => t.email)
    : typingUsers;

  // Debug logging
  useEffect(() => {
    console.log('[Chat] Messages:', messages.length, 'Active channel:', activeChannelId, 'Filtered:', currentMessages.length);
    console.log('[Chat] Message channelIds:', messages.map(m => m.channelId).filter((v, i, a) => a.indexOf(v) === i));
  }, [messages, activeChannelId, currentMessages.length]);

  // ═══════════════ RENDER ═══════════════

  // ─── Sidebar width ───
  const sidebarWidth = 280;
  const threadWidth = 360;

  // ─── Color tokens ───
  const sidebarBg    = isDark ? '#1a1d27' : '#f8fafd';
  const mainBg       = isDark ? '#0f1117' : '#ffffff';
  const headerBg     = isDark ? '#16192a' : '#ffffff';
  const inputBg      = isDark ? alpha('#fff', 0.05) : '#f1f3f4';
  const borderColor  = isDark ? 'rgba(255,255,255,0.18)' : '#bdc1c6';
  const hoverBg      = isDark ? 'rgba(255,255,255,0.07)' : '#e8eaed';
  const scrollbarThumb = isDark ? '#4a4d5a' : '#9aa0a6';
  const scrollbarTrack = isDark ? '#1e2130' : '#f1f3f4';

  // ─── UserAvatar sub-component ───
  const UserAvatar = ({ sender, size = 36, showStatus = false, status }: { sender: any; size?: number; showStatus?: boolean; status?: string }) => (
    <Badge
      overlap="circular"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      variant="dot"
      invisible={!showStatus}
      sx={{ '& .MuiBadge-dot': { width: 10, height: 10, borderRadius: '50%', border: `2px solid ${mainBg}`, backgroundColor: getStatusColor(status || 'offline') } }}
    >
      <Avatar
        sx={{
          width: size, height: size, fontSize: size * 0.4,
          background: getAvatarGradient(sender?.email || ''),
          fontWeight: 600, color: '#fff',
        }}
      >
        {getInitials(sender)}
      </Avatar>
    </Badge>
  );

  // ─── Message bubble ───
  const renderMessage = (msg: ChatMessage, idx: number, list: ChatMessage[], isThread = false) => {
    if (msg.isDeleted) return (
      <Box key={msg.id} sx={{ px: 3, py: 0.5 }}>
        <Typography variant="body2" sx={{ color: alpha(isDark ? '#fff' : '#000', 0.3), fontStyle: 'italic', pl: '52px' }}>
          This message was deleted
        </Typography>
      </Box>
    );

    const prevMsg = idx > 0 ? list[idx - 1] : null;
    const showDateSep = !prevMsg || !isSameDay(prevMsg.createdAt, msg.createdAt);
    const isGrouped = prevMsg && !showDateSep && isSameMinuteAndSender(prevMsg, msg) && !prevMsg.isDeleted;
    const isOwnMsg = msg.sender.id === currentUserId;
    const isHovered = hoverMsgId === msg.id;

    return (
      <React.Fragment key={msg.id}>
        {showDateSep && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5, my: 0.5 }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: borderColor }} />
            <Box sx={{
              px: 1.5, py: 0.3, borderRadius: 10,
              bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.04),
              border: `1px solid ${borderColor}`,
            }}>
              <Typography sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1 }}>
                {formatDate(msg.createdAt)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, height: '1px', bgcolor: borderColor }} />
          </Box>
        )}
        <Box
          onMouseEnter={() => setHoverMsgId(msg.id)}
          onMouseLeave={() => setHoverMsgId(null)}
          onContextMenu={(e) => { e.preventDefault(); setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, message: msg }); }}
          sx={{
            px: 2.5, py: isGrouped ? 0.25 : 1,
            position: 'relative',
            backgroundColor: msg.isPriority ? alpha(accent.main, 0.06) : (isHovered ? alpha(isDark ? '#fff' : '#000', 0.02) : 'transparent'),
            borderLeft: msg.isPriority ? `3px solid ${accent.main}` : '3px solid transparent',
            transition: 'background-color 0.15s ease',
            '&:hover': { backgroundColor: alpha(isDark ? '#fff' : '#000', 0.03) },
          }}
        >
          {/* Hover action bar */}
          {isHovered && !isThread && (
            <Fade in>
              <Paper
                elevation={4}
                sx={{
                  position: 'absolute', top: -18, right: 20, zIndex: 10,
                  display: 'flex', gap: 0, borderRadius: '10px',
                  border: `1px solid ${borderColor}`,
                  backgroundColor: isDark ? '#1e2130' : '#fff',
                  boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.12)',
                  overflow: 'hidden',
                }}
              >
                {QUICK_REACTIONS.slice(0, 4).map(emoji => (
                  <Tooltip key={emoji} title={emoji}>
                    <IconButton size="small" onClick={() => handleReaction(msg.id, emoji)}
                      sx={{ fontSize: 15, p: 0.6, borderRadius: 0, '&:hover': { bgcolor: hoverBg } }}>{emoji}</IconButton>
                  </Tooltip>
                ))}
                <Box sx={{ width: '1px', bgcolor: borderColor, my: 0.75 }} />
                <Tooltip title="Reply in thread">
                  <IconButton size="small" onClick={() => openThread(msg)}
                    sx={{ p: 0.6, borderRadius: 0, '&:hover': { bgcolor: hoverBg, color: accent.main } }}>
                    <ThreadIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reply">
                  <IconButton size="small" onClick={() => setReplyTo(msg)}
                    sx={{ p: 0.6, borderRadius: 0, '&:hover': { bgcolor: hoverBg, color: accent.main } }}>
                    <ReplyIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={msg.isPinned ? 'Unpin' : 'Pin'}>
                  <IconButton size="small" onClick={() => msg.isPinned ? handleUnpin(msg.id) : handlePin(msg.id)}
                    sx={{ p: 0.6, borderRadius: 0, color: msg.isPinned ? accent.main : 'inherit', '&:hover': { bgcolor: hoverBg, color: accent.main } }}>
                    <PinIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="More">
                  <IconButton size="small" onClick={(e) => setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, message: msg })}
                    sx={{ p: 0.6, borderRadius: 0, '&:hover': { bgcolor: hoverBg } }}>
                    <MoreIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Paper>
            </Fade>
          )}

          {/* DM View: Own messages on right, other messages on left */}
          {mainView === 'dm' && isOwnMsg ? (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', justifyContent: 'flex-end' }}>
              {/* Own message content (right side) - no avatar */}
              <Box sx={{ maxWidth: '65%', minWidth: 0 }}>
                {/* Time (shown on hover for grouped messages) */}
                {!isGrouped && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, justifyContent: 'flex-end' }}>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem', fontWeight: 500 }}>
                      {formatTime(msg.createdAt)}
                    </Typography>
                    {msg.isEdited && <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem' }}>(edited)</Typography>}
                  </Box>
                )}

                {/* Message bubble - Modern Teams style */}
                <Paper elevation={0} sx={{
                  px: 2, py: 1.25, 
                  borderRadius: '18px 18px 4px 18px',
                  background: `linear-gradient(135deg, ${accent.main} 0%, ${alpha(accent.main, 0.85)} 100%)`,
                  color: '#fff',
                  display: 'inline-block', 
                  maxWidth: '100%',
                  boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.15)',
                    transform: 'translateY(-1px)',
                  },
                }}>
                  {/* Content */}
                  {msg.messageType === 'text' && (
                    <Typography variant="body2" sx={{ 
                      color: '#fff', 
                      lineHeight: 1.6, 
                      wordBreak: 'break-word', 
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.9rem',
                      fontWeight: 400,
                      letterSpacing: '0.01em',
                    }}>
                      {msg.content}
                    </Typography>
                  )}

                  {/* File / image attachment */}
                  {(msg.messageType === 'file' || msg.messageType === 'image' || msg.messageType === 'voice') && (
                    <Box>
                      {msg.messageType === 'image' && msg.fileUrl ? (
                        <Box
                          component="img"
                          src={`${apiUrl}/chat/files/${msg.fileUrl.split('/').pop()}`}
                          sx={{ 
                            maxWidth: 300, 
                            maxHeight: 220, 
                            borderRadius: 2, 
                            cursor: 'pointer', 
                            display: 'block',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            transition: 'transform 0.2s ease',
                            '&:hover': { transform: 'scale(1.02)' },
                          }}
                          onClick={() => window.open(`${apiUrl}/chat/files/${msg.fileUrl!.split('/').pop()}`, '_blank')}
                        />
                      ) : msg.messageType === 'voice' ? (
                        <Box component="audio" controls src={`${apiUrl}/chat/files/${(msg.fileUrl || '').split('/').pop()}`} sx={{ maxWidth: 260 }} />
                      ) : (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5, 
                          cursor: 'pointer',
                          p: 1,
                          borderRadius: 1.5,
                          bgcolor: 'rgba(255,255,255,0.15)',
                          transition: 'background-color 0.2s',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                        }}
                          onClick={() => window.open(`${apiUrl}/chat/files/${(msg.fileUrl || '').split('/').pop()}`, '_blank')}
                        >
                          <FileIcon sx={{ fontSize: 28, color: '#fff' }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ 
                              fontWeight: 600, 
                              color: '#fff', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              whiteSpace: 'nowrap',
                            }}>
                              {msg.fileName || 'File'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.72rem' }}>
                              {formatFileSize(msg.fileSize || 0)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {msg.content && msg.content !== msg.fileName && (
                        <Typography variant="body2" sx={{ mt: 1, color: '#fff', fontSize: '0.9rem' }}>{msg.content}</Typography>
                      )}
                    </Box>
                  )}
                </Paper>
              </Box>
            </Box>
          ) : (
            /* Channel view OR other user's messages in DM (left side) */
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
              {/* Avatar or spacer */}
              {isGrouped ? (
                <Box sx={{ width: 38, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem', opacity: isHovered ? 1 : 0, transition: 'opacity 0.15s' }}>
                    {formatTime(msg.createdAt)}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ alignSelf: 'flex-end', mb: 0.5 }}>
                  <UserAvatar sender={msg.sender} />
                </Box>
              )}

              <Box sx={{ flex: 1, minWidth: 0, maxWidth: mainView === 'dm' ? '65%' : '100%' }}>
                {/* Name + time (only for first in group) */}
                {!isGrouped && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {mainView !== 'dm' && (
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.85rem' }}>
                        {getUserDisplayName(msg.sender)}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem', fontWeight: 500 }}>
                      {formatTime(msg.createdAt)}
                    </Typography>
                    {msg.isPriority && <Chip size="small" label="PRIORITY" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, backgroundColor: accent.main, color: '#fff' }} />}
                    {msg.isEdited && <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem' }}>(edited)</Typography>}
                    {msg.isPinned && <PinIcon sx={{ fontSize: 12, color: accent.main, transform: 'rotate(45deg)' }} />}
                  </Box>
                )}

                {/* Reply quote */}
                {msg.replyTo && (
                  <Box sx={{ borderLeft: `2px solid ${accent.main}`, pl: 1, mb: 0.5, py: 0.25, opacity: 0.8 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: accent.main }}>
                      {getUserDisplayName(msg.replyTo.sender)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 400 }}>
                      {msg.replyTo.content}
                    </Typography>
                  </Box>
                )}

                {/* Message bubble - Modern Teams style for other users */}
                {(msg.messageType === 'text' || msg.messageType === 'file' || msg.messageType === 'image' || msg.messageType === 'voice') && (
                  <Paper elevation={0} sx={{
                    px: 2, py: 1.25,
                    borderRadius: '18px 18px 18px 4px',
                    bgcolor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.04),
                    display: 'inline-block',
                    maxWidth: '100%',
                    boxShadow: isDark ? '0 1px 4px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.08)',
                    border: `1px solid ${isDark ? alpha('#fff', 0.05) : alpha('#000', 0.06)}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: isDark ? alpha('#fff', 0.1) : alpha('#000', 0.06),
                      boxShadow: isDark ? '0 2px 6px rgba(0,0,0,0.25)' : '0 2px 6px rgba(0,0,0,0.1)',
                    },
                  }}>
                    {/* Content */}
                    {msg.messageType === 'text' && (
                      <Typography variant="body2" sx={{ 
                        color: 'text.primary', 
                        lineHeight: 1.6, 
                        wordBreak: 'break-word', 
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.9rem',
                        fontWeight: 400,
                        letterSpacing: '0.01em',
                      }}>
                        {msg.content}
                      </Typography>
                    )}

                    {/* File / image attachment */}
                    {(msg.messageType === 'file' || msg.messageType === 'image' || msg.messageType === 'voice') && (
                      <Box>
                        {msg.messageType === 'image' && msg.fileUrl ? (
                          <Box
                            component="img"
                            src={`${apiUrl}/chat/files/${msg.fileUrl.split('/').pop()}`}
                            sx={{ 
                              maxWidth: 300, 
                              maxHeight: 220, 
                              borderRadius: 2, 
                              cursor: 'pointer', 
                              display: 'block',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                              transition: 'transform 0.2s ease',
                              '&:hover': { transform: 'scale(1.02)' },
                            }}
                            onClick={() => window.open(`${apiUrl}/chat/files/${msg.fileUrl!.split('/').pop()}`, '_blank')}
                          />
                        ) : msg.messageType === 'voice' ? (
                          <Box component="audio" controls src={`${apiUrl}/chat/files/${(msg.fileUrl || '').split('/').pop()}`} sx={{ maxWidth: 260 }} />
                        ) : (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1.5, 
                            cursor: 'pointer',
                            p: 1,
                            borderRadius: 1.5,
                            bgcolor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.04),
                            transition: 'background-color 0.2s',
                            '&:hover': { bgcolor: isDark ? alpha('#fff', 0.12) : alpha('#000', 0.08) },
                          }}
                            onClick={() => window.open(`${apiUrl}/chat/files/${(msg.fileUrl || '').split('/').pop()}`, '_blank')}
                          >
                            <FileIcon sx={{ fontSize: 28, color: accent.main }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ 
                                fontWeight: 600, 
                                color: 'text.primary',
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap',
                              }}>
                                {msg.fileName || 'File'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                                {formatFileSize(msg.fileSize || 0)}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        {msg.content && msg.content !== msg.fileName && (
                          <Typography variant="body2" sx={{ mt: 1, color: 'text.primary', fontSize: '0.9rem' }}>{msg.content}</Typography>
                        )}
                      </Box>
                    )}
                  </Paper>
                )}

                {/* Poll (for channels only) */}
                {msg.messageType === 'poll' && msg.poll && mainView !== 'dm' && (
                <Paper variant="outlined" sx={{ p: 2, mt: 0.5, borderRadius: 2, maxWidth: 400, border: `1px solid ${accent.main}30` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <PollIcon sx={{ color: accent.main, fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{msg.poll.question}</Typography>
                  </Box>
                  {msg.poll.options.map((opt, i) => {
                    const votes = msg.poll!.votes?.filter(v => v.optionIndex === i) || [];
                    const voteCount = votes.length;
                    const totalVotes = msg.poll!.totalVotes || 1;
                    const pct = Math.round((voteCount / totalVotes) * 100) || 0;
                    const myVote = votes.some(v => v.userId === currentUserId);
                    return (
                      <Box key={i} onClick={() => handleVotePoll(msg.poll!.id, i)}
                        sx={{
                          position: 'relative', borderRadius: 1, mb: 0.5, p: 1, cursor: 'pointer',
                          border: myVote ? `2px solid ${accent.main}` : `1px solid ${borderColor}`,
                          overflow: 'hidden', '&:hover': { borderColor: accent.main },
                        }}
                      >
                        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, backgroundColor: alpha(accent.main, 0.12), transition: 'width 0.3s' }} />
                        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: myVote ? 700 : 400 }}>{opt}</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: accent.main }}>{pct}%</Typography>
                        </Box>
                      </Box>
                    );
                  })}
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                    {msg.poll.totalVotes || 0} vote{(msg.poll.totalVotes || 0) !== 1 ? 's' : ''} {msg.poll.multiSelect ? '· Multiple choice' : ''}
                  </Typography>
                </Paper>
              )}

              {/* Reactions */}
              {msg.reactions && msg.reactions.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                  {msg.reactions.map((r) => (
                    <Chip
                      key={r.emoji}
                      label={`${r.emoji} ${r.count}`}
                      size="small"
                      onClick={() => handleReaction(msg.id, r.emoji)}
                      sx={{
                        height: 24, fontSize: '0.75rem',
                        backgroundColor: r.users?.some(u => u.id === currentUserId) ? alpha(accent.main, 0.15) : inputBg,
                        border: r.users?.some(u => u.id === currentUserId) ? `1px solid ${accent.main}` : 'none',
                        cursor: 'pointer', '&:hover': { backgroundColor: alpha(accent.main, 0.2) },
                      }}
                    />
                  ))}
                  <IconButton size="small" onClick={(e) => { setEmojiTarget(msg.id); setEmojiAnchor(e.currentTarget); }} sx={{ width: 24, height: 24, p: 0 }}>
                    <Typography sx={{ fontSize: 14 }}>+</Typography>
                  </IconButton>
                </Box>
              )}

              {/* Thread link */}
              {!isThread && msg.replyCount && msg.replyCount > 0 && (
                <Box
                  onClick={() => openThread(msg)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                >
                  <ThreadIcon sx={{ fontSize: 14, color: accent.main }} />
                  <Typography variant="caption" sx={{ color: accent.main, fontWeight: 600 }}>
                    {msg.replyCount} {msg.replyCount === 1 ? 'reply' : 'replies'}
                  </Typography>
                  {msg.lastReplyAt && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      · Last reply {formatTime(msg.lastReplyAt)}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Read receipts (only own messages) */}
              {isOwnMsg && msg.readBy && msg.readBy.length > 0 && (
                <Tooltip title={`Read by ${msg.readBy.map(r => r.email.split('@')[0]).join(', ')}`}>
                  <ReadIcon sx={{ fontSize: 14, color: accent.main, mt: 0.25 }} />
                </Tooltip>
              )}
              </Box>
            </Box>
          )}
        </Box>
      </React.Fragment>
    );
  };

  // ─── Composer ───
  const renderComposer = (
    value: string,
    onChange: (v: string) => void,
    onSend: () => void,
    placeholder: string,
  ) => (
    <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
      {/* Pasted image preview */}
      {pastedImagePreviewUrl && (
        <Fade in>
          <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, p: 1,
            borderRadius: 1.5, border: `1px solid ${borderColor}`, bgcolor: inputBg, position: 'relative',
          }}>
            <Box
              component="img"
              src={pastedImagePreviewUrl}
              sx={{ maxHeight: 120, maxWidth: 200, borderRadius: 1, objectFit: 'contain', display: 'block' }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary' }}>Pasted image</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>{pastedImageFile?.name}</Typography>
            </Box>
            <IconButton size="small" onClick={() => {
              setPastedImageFile(null);
              if (pastedImagePreviewUrl) { URL.revokeObjectURL(pastedImagePreviewUrl); setPastedImagePreviewUrl(null); }
            }} sx={{ p: 0.25, flexShrink: 0 }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Fade>
      )}

      {/* Reply bar */}
      {replyTo && (
        <Fade in>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 1, px: 1.5, py: 0.75,
            borderRadius: 1.5, bgcolor: alpha(accent.main, 0.07), borderLeft: `3px solid ${accent.main}`,
          }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, color: accent.main, fontSize: '0.72rem' }}>
                ↩ Replying to {getUserDisplayName(replyTo.sender)}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {replyTo.content}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setReplyTo(null)} sx={{ p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Fade>
      )}

      {/* Composer card */}
      <Box sx={{
        border: `1.5px solid ${borderColor}`,
        borderRadius: '12px',
        backgroundColor: inputBg,
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:focus-within': {
          borderColor: alpha(accent.main, 0.5),
          boxShadow: `0 0 0 3px ${alpha(accent.main, 0.08)}`,
        },
      }}>
        {/* Format toolbar — shown when visible */}
        {showFormatBar && (
          <Box sx={{ display: 'flex', gap: 0, px: 1, pt: 0.75, pb: 0.5, borderBottom: `1px solid ${borderColor}` }}>
            {[<BoldIcon/>, <ItalicIcon/>, <StrikeIcon/>, <CodeIcon/>, <ListIcon/>].map((icon, i) => (
              <IconButton key={i} size="small" sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: accent.main } }}>
                {React.cloneElement(icon, { sx: { fontSize: 16 } })}
              </IconButton>
            ))}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Tooltip title="Priority">
              <IconButton size="small" onClick={() => setIsPriority(!isPriority)}
                sx={{ p: 0.5, color: isPriority ? accent.main : 'text.secondary' }}>
                <PriorityIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Text area */}
        <TextField
          fullWidth multiline maxRows={8}
          variant="standard"
          placeholder={pastedImagePreviewUrl ? 'Add a caption (optional)…' : placeholder}
          value={value}
          onChange={(e) => { onChange(e.target.value); handleTyping(); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); }
          }}
          onPaste={handlePaste}
          InputProps={{ disableUnderline: true }}
          inputProps={{ onPaste: handlePaste, 'data-composer-textarea': 'true' } as any}
          sx={{ px: 1.5, pt: 1.25, pb: 0.5, '& .MuiInputBase-input': { fontSize: '0.88rem', lineHeight: 1.55 } }}
        />

        {/* Bottom toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, px: 0.75, pb: 0.75, pt: 0 }}>
          <Tooltip title="Formatting">
            <IconButton size="small" onClick={() => setShowFormatBar(!showFormatBar)}
              sx={{ p: 0.6, color: showFormatBar ? accent.main : 'text.secondary', '&:hover': { color: accent.main } }}>
              <BoldIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Attach file">
            <IconButton size="small" onClick={() => fileInputRef.current?.click()}
              sx={{ p: 0.6, color: 'text.secondary', '&:hover': { color: accent.main } }}>
              {uploadingFile ? <CircularProgress size={15} /> : <AttachIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Emoji">
            <IconButton size="small" onClick={(e) => { setEmojiTarget('input'); setEmojiAnchor(e.currentTarget); }}
              sx={{ p: 0.6, color: 'text.secondary', '&:hover': { color: accent.main } }}>
              <EmojiIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Poll">
            <IconButton size="small" onClick={() => setPollDialogOpen(true)}
              sx={{ p: 0.6, color: 'text.secondary', '&:hover': { color: accent.main } }}>
              <PollIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          {value.trim() ? (
            <IconButton onClick={onSend} size="small" sx={{
              p: 0, width: 30, height: 30, borderRadius: '8px',
              bgcolor: accent.main, color: '#fff',
              '&:hover': { bgcolor: accent.dark },
              transition: 'background-color 0.15s',
            }}>
              <SendIcon sx={{ fontSize: 16 }} />
            </IconButton>
          ) : pastedImagePreviewUrl ? (
            <IconButton onClick={onSend} size="small" sx={{
              p: 0, width: 30, height: 30, borderRadius: '8px',
              bgcolor: accent.main, color: '#fff',
              '&:hover': { bgcolor: accent.dark },
              transition: 'background-color 0.15s',
            }}>
              <SendIcon sx={{ fontSize: 16 }} />
            </IconButton>
          ) : (
            <IconButton size="small" onClick={isRecording ? stopRecording : startRecording}
              sx={{ p: 0.6, color: isRecording ? '#ef4444' : 'text.secondary', transition: 'color 0.15s' }}>
              {isRecording ? <StopIcon sx={{ fontSize: 18 }} /> : <MicIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          )}
        </Box>
      </Box>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files?.[0]) handleFileUpload(e.target.files[0]); e.target.value = ''; }}
      />
    </Box>
  );

  // ═══════════════ LAYOUT ═══════════════

  return (
    <Box sx={{
      display: 'flex', height: '100%', width: '100%', overflow: 'hidden',
      backgroundColor: mainBg,
      fontFamily: '"Inter", "SF Pro Text", system-ui, -apple-system, sans-serif',
      fontSize: '0.875rem',
    }}>

      {/* ════════════ LEFT SIDEBAR ════════════ */}
      <Box sx={{
        width: sidebarWidth, flexShrink: 0, display: 'flex', flexDirection: 'column',
        backgroundColor: sidebarBg, borderRight: `1px solid ${borderColor}`,
      }}>
        {/* Sidebar header */}
        <Box sx={{
          px: 2, pt: 2, pb: 1.5,
          borderBottom: `1px solid ${borderColor}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{
              width: 30, height: 30, borderRadius: '8px',
              background: `linear-gradient(135deg, ${accent.main}, ${accent.dark})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 2px 8px ${alpha(accent.main, 0.4)}`,
            }}>
              <GroupsIcon sx={{ fontSize: 16, color: '#fff' }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', letterSpacing: '-0.02em', color: 'text.primary' }}>
              Team Chat
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: connected ? '#22c55e' : '#94a3b8',
              boxShadow: connected ? '0 0 0 2px rgba(34,197,94,0.2)' : 'none',
              transition: 'all 0.3s',
            }} />
          </Box>
          <Tabs
            value={sidebarTab}
            onChange={(_, v) => setSidebarTab(v)}
            variant="fullWidth"
            sx={{
              minHeight: 30,
              bgcolor: isDark ? alpha('#fff', 0.05) : alpha('#000', 0.05),
              borderRadius: 1.5,
              p: '3px',
              '& .MuiTabs-flexContainer': { gap: '3px' },
              '& .MuiTab-root': {
                minHeight: 26, py: 0, fontSize: '0.72rem', fontWeight: 600,
                textTransform: 'none', borderRadius: 1, zIndex: 1,
                color: 'text.secondary',
                '&.Mui-selected': { color: 'text.primary', bgcolor: isDark ? alpha('#fff', 0.1) : '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
              },
              '& .MuiTabs-indicator': { display: 'none' },
            }}
          >
            <Tab value="dms" label="Chat" />
            <Tab value="channels" label="Group" />
          </Tabs>

          {/* Current user status */}
          <Box 
            onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
            sx={{ 
              display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, p: 1, borderRadius: 1.5,
              bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.03),
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.06) },
            }}
          >
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <Box sx={{
                  width: 10, height: 10, borderRadius: '50%',
                  bgcolor: getStatusColor(currentUserStatus),
                  border: `2px solid ${sidebarBg}`,
                }} />
              }
            >
              <Avatar
                sx={{
                  width: 28, height: 28, fontSize: '0.7rem', fontWeight: 700,
                  background: getAvatarGradient(user?.email || ''),
                }}
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
            </Badge>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {statusMessage || getStatusLabel(currentUserStatus)}
              </Typography>
            </Box>
            <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </Box>
        </Box>

        {/* Status selector menu */}
        <Menu
          anchorEl={statusMenuAnchor}
          open={Boolean(statusMenuAnchor)}
          onClose={() => setStatusMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              mt: 0.5, minWidth: 220, borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }
          }}
        >
          <MenuItem onClick={() => handleStatusChange('available')} sx={{ gap: 1.5, py: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981', flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Available</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Active and available</Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleStatusChange('busy')} sx={{ gap: 1.5, py: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444', flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Busy</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Do not disturb</Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleStatusChange('away')} sx={{ gap: 1.5, py: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b', flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Away</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Currently away</Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleStatusChange('dnd')} sx={{ gap: 1.5, py: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#dc2626', flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Do not disturb</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Notifications muted</Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleStatusChange('brb')} sx={{ gap: 1.5, py: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#8b5cf6', flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Be right back</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Away temporarily</Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleStatusChange('invisible')} sx={{ gap: 1.5, py: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#6b7280', flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Appear offline</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Invisible to others</Typography>
            </Box>
          </MenuItem>
        </Menu>

        {/* Sidebar content */}
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pt: 0.5,
          scrollbarWidth: 'thin', scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { background: scrollbarTrack },
          '&::-webkit-scrollbar-thumb': { background: scrollbarThumb, borderRadius: 3 },
        }}>
          {sidebarTab === 'dms' && (
            <>
              {dmRooms.length > 0 && (
                <Box sx={{ px: 2, py: 0.75 }}>
                  <Typography sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.disabled', fontSize: '0.6rem' }}>
                    Conversations
                  </Typography>
                </Box>
              )}
              <List dense disablePadding sx={{ px: 1 }}>
                {dmRooms.map(room => {
                  const isActive = mainView === 'dm' && activeDmRoom?.id === room.id;
                  const otherUserOnline = onlineUsers.find(u => u.id === room.otherUser.id);
                  return (
                    <ListItemButton
                      key={room.id}
                      selected={isActive}
                      onClick={() => handleOpenDm(room.otherUser.id)}
                      sx={{
                        borderRadius: '6px', mb: 0.25, py: 0.6, px: 1,
                        '&.Mui-selected': {
                          bgcolor: alpha(accent.main, isDark ? 0.18 : 0.1),
                          '&:hover': { bgcolor: alpha(accent.main, isDark ? 0.22 : 0.14) },
                        },
                        '&:hover': { bgcolor: hoverBg },
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 44 }}>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            otherUserOnline ? (
                              <Box sx={{
                                width: 10, height: 10, borderRadius: '50%',
                                bgcolor: getStatusColor(otherUserOnline.status),
                                border: `2px solid ${sidebarBg}`,
                              }} />
                            ) : null
                          }
                        >
                          <Avatar
                            sx={{
                              width: 34, height: 34,
                              background: getAvatarGradient(room.otherUser.email),
                              color: '#fff', fontSize: '0.95rem', fontWeight: 700,
                            }}
                          >
                            {getUserDisplayName(room.otherUser).charAt(0).toUpperCase()}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <Box sx={{ overflow: 'hidden', mr: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.84rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {getUserDisplayName(room.otherUser)}
                        </Typography>
                        {room.lastMessage && (
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {typeof room.lastMessage === 'string' ? room.lastMessage : (room.lastMessage.messageType === 'file' ? '📎 File' : room.lastMessage.content)}
                          </Typography>
                        )}
                      </Box>
                      {room.unreadCount > 0 && (
                        <Chip
                          label={room.unreadCount}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: accent.main, color: '#fff', '& .MuiChip-label': { px: 0.75 } }}
                        />
                      )}
                    </ListItemButton>
                  );
                })}
                {dmRooms.length === 0 && (
                  <Box sx={{ px: 1, py: 2, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>No conversations yet</Typography>
                  </Box>
                )}
              </List>

              {/* Team members (domain-filtered, excluding those with conversations) */}
              {teamUsersWithoutConversations.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ px: 2, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.disabled', fontSize: '0.6rem' }}>
                      Team — {teamUsersWithoutConversations.length}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2, pb: 0.5 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Filter people..."
                      value={userFilter}
                      onChange={(e) => setUserFilter(e.target.value)}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ fontSize: 16, color: 'text.disabled', mr: 0.5 }} />,
                        sx: { fontSize: '0.8rem', borderRadius: 1 },
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02) } }}
                    />
                  </Box>
                  <List dense disablePadding sx={{ px: 1 }}>
                    {teamUsersWithoutConversations.map((u) => (
                      <ListItemButton
                        key={u.id}
                        onClick={() => handleOpenDm(u.id)}
                        sx={{
                          borderRadius: '6px', mb: 0.25, py: 0.6, px: 1,
                          '&:hover': { bgcolor: hoverBg },
                        }}
                      >
                        <ListItemAvatar sx={{ minWidth: 44 }}>
                          <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            badgeContent={
                              <Box sx={{
                                width: 10, height: 10, borderRadius: '50%',
                                bgcolor: getStatusColor(u.status),
                                border: `2px solid ${sidebarBg}`,
                              }} />
                            }
                          >
                            <Avatar
                              sx={{
                                width: 34, height: 34,
                                background: getAvatarGradient(u.email),
                                color: '#fff', fontSize: '0.95rem', fontWeight: 700,
                              }}
                            >
                              {getUserDisplayName(u).charAt(0).toUpperCase()}
                            </Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <Box sx={{ overflow: 'hidden' }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.84rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {getUserDisplayName(u)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {u.statusMessage || getStatusLabel(u.status)}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}

          {sidebarTab === 'channels' && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 0.75 }}>
                <Typography sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.disabled', fontSize: '0.6rem' }}>
                  Groups
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {bulkDeleteMode ? (
                    <>
                      <Tooltip title="Select all">
                        <IconButton size="small" onClick={selectAllChannels}
                          sx={{ p: 0.25, width: 18, height: 18, color: 'text.secondary', '&:hover': { color: accent.main } }}>
                          <AddIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={`Delete ${selectedChannelIds.size} selected`}>
                        <IconButton size="small" onClick={handleBulkDelete} disabled={selectedChannelIds.size === 0}
                          sx={{ p: 0.25, width: 18, height: 18, color: selectedChannelIds.size > 0 ? 'error.main' : 'text.disabled', '&:hover': { color: 'error.dark' } }}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton size="small" onClick={() => { setBulkDeleteMode(false); setSelectedChannelIds(new Set()); }}
                          sx={{ p: 0.25, width: 18, height: 18, color: 'text.secondary', '&:hover': { color: accent.main } }}>
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Bulk delete">
                        <IconButton size="small" onClick={() => setBulkDeleteMode(true)}
                          sx={{ p: 0.25, width: 18, height: 18, color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="New group">
                        <IconButton size="small" onClick={() => setChannelDialogOpen(true)}
                          sx={{ p: 0.25, width: 18, height: 18, color: 'text.secondary', '&:hover': { color: accent.main } }}>
                          <AddIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
              <List dense disablePadding sx={{ px: 1 }}>
                {channels.map(ch => {
                  const isActive = mainView === 'channel' && activeChannelId === ch.id;
                  const isSelected = selectedChannelIds.has(ch.id);
                  return (
                    <Box key={ch.id} sx={{ position: 'relative', '&:hover .delete-btn': { opacity: bulkDeleteMode ? 0 : 1 } }}>
                      <ListItemButton
                        selected={isActive}
                        onClick={() => {
                          if (bulkDeleteMode && !ch.isDefault) {
                            toggleChannelSelection(ch.id);
                          } else {
                            setActiveChannelId(ch.id);
                            setMainView('channel');
                          }
                        }}
                        sx={{
                          borderRadius: '6px', mb: 0.25, py: 0.4, px: 1,
                          transition: 'all 0.15s',
                          bgcolor: bulkDeleteMode && isSelected ? alpha(accent.main, 0.12) : 'transparent',
                          '&.Mui-selected': {
                            bgcolor: alpha(accent.main, isDark ? 0.18 : 0.1),
                            '& .MuiTypography-root': { color: isDark ? '#fff' : accent.dark },
                            '&:hover': { bgcolor: alpha(accent.main, isDark ? 0.22 : 0.14) },
                          },
                          '&:hover': { bgcolor: hoverBg },
                        }}
                      >
                        {bulkDeleteMode && !ch.isDefault && (
                          <Checkbox
                            checked={isSelected}
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleChannelSelection(ch.id)}
                            sx={{ p: 0, mr: 0.5 }}
                          />
                        )}
                        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mr: 0.5, lineHeight: 1 }}>
                          {ch.isPrivate ? '🔒' : '#'}
                        </Typography>
                        <ListItemText
                          primary={ch.name}
                          primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: isActive ? 700 : 500, color: isActive ? 'text.primary' : 'text.secondary' }}
                        />
                        {ch.isDefault && (
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: accent.main, flexShrink: 0 }} />
                        )}
                      </ListItemButton>
                      {!ch.isDefault && (
                        <IconButton
                          className="delete-btn"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleDeleteChannel(ch.id, ch.name); }}
                          sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            bgcolor: 'background.paper',
                            '&:hover': { bgcolor: 'error.main', color: 'white' },
                            width: 24,
                            height: 24,
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
                {channels.length === 0 && (
                  <Box sx={{ px: 1, py: 2, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>No groups yet</Typography>
                    <Typography
                      variant="caption"
                      onClick={() => setChannelDialogOpen(true)}
                      sx={{ color: accent.main, cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                    >
                      Create one
                    </Typography>
                  </Box>
                )}
              </List>
            </>
          )}
        </Box>
      </Box>

      {/* ════════════ MAIN AREA ════════════ */}
      <Box 
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Drag overlay */}
        {isDraggingFile && (
          <Box sx={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 9999,
            bgcolor: isDark ? alpha(accent.main, 0.12) : alpha(accent.main, 0.08),
            backdropFilter: 'blur(4px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            border: `3px dashed ${accent.main}`,
            borderRadius: 2,
            pointerEvents: 'none',
          }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: '50%',
              bgcolor: isDark ? alpha(accent.main, 0.2) : alpha(accent.main, 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 20px ${alpha(accent.main, 0.3)}`,
            }}>
              <AttachIcon sx={{ fontSize: 40, color: accent.main }} />
            </Box>
            <Typography sx={{ 
              fontSize: '1.2rem', 
              fontWeight: 700, 
              color: accent.main,
              textShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
            }}>
              Drop file to upload
            </Typography>
            <Typography sx={{ 
              fontSize: '0.85rem', 
              color: 'text.secondary',
              fontWeight: 500,
            }}>
              Max 25MB • Images, PDFs, Documents
            </Typography>
          </Box>
        )}

        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 0,
          borderBottom: `1px solid ${borderColor}`,
          backgroundColor: headerBg,
          minHeight: 54,
          backdropFilter: 'blur(8px)',
        }}>
          {mainView === 'channel' && activeChannel ? (
            <>
              <Box sx={{
                width: 32, height: 32, borderRadius: '8px',
                bgcolor: isDark ? alpha(accent.main, 0.15) : alpha(accent.main, 0.1),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {activeChannel.isPrivate
                  ? <LockIcon sx={{ fontSize: 15, color: accent.main }} />
                  : <TagIcon sx={{ fontSize: 16, color: accent.main }} />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                  {activeChannel.name}
                </Typography>
                {activeChannel.description && (
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {activeChannel.description}
                  </Typography>
                )}
              </Box>
            </>
          ) : mainView === 'dm' && activeDmRoom ? (
            <>
              <UserAvatar sender={activeDmRoom.otherUser} size={32} showStatus status={onlineUsers.find(u => u.id === activeDmRoom.otherUser.id)?.status} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                  {getUserDisplayName(activeDmRoom.otherUser)}
                </Typography>
                <Typography sx={{ color: onlineUsers.find(u => u.id === activeDmRoom.otherUser.id)?.status && onlineUsers.find(u => u.id === activeDmRoom.otherUser.id)?.status !== 'offline' && onlineUsers.find(u => u.id === activeDmRoom.otherUser.id)?.status !== 'invisible' ? '#22c55e' : 'text.disabled', fontSize: '0.68rem', lineHeight: 1.2 }}>
                  {(() => {
                    const otherUserStatus = onlineUsers.find(u => u.id === activeDmRoom.otherUser.id)?.status;
                    if (otherUserStatus && otherUserStatus !== 'offline' && otherUserStatus !== 'invisible') {
                      return `● ${getStatusLabel(otherUserStatus)}`;
                    }
                    return activeDmRoom.otherUser.email;
                  })()}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography sx={{ fontWeight: 700, flex: 1, color: 'text.secondary' }}>Select a channel or conversation</Typography>
          )}

          {/* Header actions */}
          {mainView === 'channel' && activeChannelId && (
            <Tooltip title="View members">
              <IconButton size="small" onClick={() => fetchChannelMembers(activeChannelId)}
                sx={{ color: 'text.secondary', p: 0.75, '&:hover': { color: accent.main } }}>
                <GroupsIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Pinned messages">
            <IconButton size="small" onClick={() => { setShowPinned(!showPinned); fetchPinnedMessages(); }}
              sx={{ color: showPinned ? accent.main : 'text.secondary', p: 0.75 }}>
              <PinIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Search messages">
            <IconButton size="small" onClick={() => setSearchOpen(!searchOpen)}
              sx={{ color: searchOpen ? accent.main : 'text.secondary', p: 0.75 }}>
              <SearchIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Search bar */}
        {searchOpen && (
          <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${borderColor}`, backgroundColor: alpha(accent.main, 0.03) }}>
            <TextField
              fullWidth size="small" variant="outlined" placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = setTimeout(() => handleSearch(e.target.value), 400);
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                endAdornment: searchLoading ? <CircularProgress size={18} /> : searchQuery ? (
                  <IconButton size="small" onClick={() => { setSearchQuery(''); setSearchResults([]); }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                ) : null,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.85rem' } }}
            />
            {searchResults.length > 0 && (
              <Box sx={{ maxHeight: 300, overflowY: 'auto', mt: 1 }}>
                {searchResults.map((msg, idx) => renderMessage(msg, idx, searchResults))}
              </Box>
            )}
          </Box>
        )}

        {/* Pinned panel */}
        {showPinned && pinnedMessages.length > 0 && (
          <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${borderColor}`, backgroundColor: alpha(accent.main, 0.03), maxHeight: 250, overflowY: 'auto' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, display: 'block', color: 'text.secondary' }}>
              📌 Pinned Messages ({pinnedMessages.length})
            </Typography>
            {pinnedMessages.map((msg, idx) => renderMessage(msg, idx, pinnedMessages))}
          </Box>
        )}

        {/* Message list */}
        <Box ref={messagesContainerRef} sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden',
          scrollbarWidth: 'thin', scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { background: scrollbarTrack },
          '&::-webkit-scrollbar-thumb': { background: scrollbarThumb, borderRadius: 3 },
        }}>
          {!connected ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 2 }}>
              <CircularProgress size={28} sx={{ color: accent.main }} />
              <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>Connecting...</Typography>
            </Box>
          ) : currentMessages.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 1.5, px: 4, textAlign: 'center' }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: '16px',
                bgcolor: isDark ? alpha(accent.main, 0.12) : alpha(accent.main, 0.08),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {mainView === 'channel'
                  ? <TagIcon sx={{ fontSize: 26, color: accent.main }} />
                  : <GroupsIcon sx={{ fontSize: 26, color: accent.main }} />}
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                {mainView === 'channel' && activeChannel ? `Welcome to #${activeChannel.name}` : 'Start a conversation'}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem', maxWidth: 280 }}>
                {mainView === 'channel' ? 'This is the very beginning of this channel. Send the first message!' : 'No messages yet — say hello!'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ py: 1 }}>
              {currentMessages.map((msg, idx) => renderMessage(msg, idx, currentMessages))}
              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>

        {/* Typing indicator - WhatsApp style */}
        {currentTyping.length > 0 && (
          <Box sx={{ px: 3, py: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {mainView === 'dm' && activeDmRoom && (
              <UserAvatar sender={activeDmRoom.otherUser} size={28} />
            )}
            <Paper elevation={0} sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1,
              borderRadius: '18px',
              bgcolor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.04),
              border: `1px solid ${isDark ? alpha('#fff', 0.05) : alpha('#000', 0.06)}`,
            }}>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {[0, 1, 2].map(i => (
                  <Box 
                    key={i} 
                    sx={{ 
                      width: 7, 
                      height: 7, 
                      borderRadius: '50%', 
                      backgroundColor: accent.main,
                      animation: 'typingBounce 1.4s infinite ease-in-out both', 
                      animationDelay: `${i * 0.16}s`,
                      '@keyframes typingBounce': {
                        '0%, 80%, 100%': { transform: 'scale(0.8)', opacity: 0.5 },
                        '40%': { transform: 'scale(1.1)', opacity: 1 },
                      },
                    }} 
                  />
                ))}
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', fontWeight: 500 }}>
                {currentTyping.map(e => e.split('@')[0]).join(', ')} {currentTyping.length === 1 ? 'is' : 'are'} typing...
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Composer */}
        {renderComposer(
          message, setMessage, handleSendMessage,
          mainView === 'channel' && activeChannel
            ? `Message #${activeChannel.name}`
            : mainView === 'dm' && activeDmRoom
            ? `Message ${getUserDisplayName(activeDmRoom.otherUser)}`
            : 'Type a message...',
        )}
      </Box>

      {/* ════════════ THREAD PANEL ════════════ */}
      {threadOpen && threadParent && (
        <Box sx={{
          width: threadWidth, flexShrink: 0, display: 'flex', flexDirection: 'column',
          borderLeft: `1px solid ${borderColor}`, backgroundColor: mainBg,
        }}>
          {/* Thread header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: `1px solid ${borderColor}` }}>
            <ThreadIcon sx={{ fontSize: 18, color: accent.main }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>Thread</Typography>
            <IconButton size="small" onClick={() => { setThreadOpen(false); setThreadParent(null); setThreadReplies([]); }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Thread parent message */}
          <Box sx={{ borderBottom: `1px solid ${borderColor}`, py: 1 }}>
            {renderMessage(threadParent, 0, [threadParent], true)}
          </Box>

          {/* Thread replies */}
          <Box sx={{ flex: 1, overflowY: 'auto', py: 1,
            scrollbarWidth: 'thin', scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
            '&::-webkit-scrollbar': { width: 5 },
            '&::-webkit-scrollbar-track': { background: scrollbarTrack },
            '&::-webkit-scrollbar-thumb': { background: scrollbarThumb, borderRadius: 3 },
          }}>
            {threadReplies.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>No replies yet</Typography>
              </Box>
            ) : (
              threadReplies.map((msg, idx) => renderMessage(msg, idx, threadReplies, true))
            )}
            <div ref={threadEndRef} />
          </Box>

          {/* Thread composer */}
          <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              border: `1px solid ${borderColor}`, borderRadius: 2, px: 1, py: 0.5,
              backgroundColor: inputBg, '&:focus-within': { borderColor: accent.main },
            }}>
              <TextField
                fullWidth variant="standard" placeholder="Reply..."
                value={threadMessage}
                onChange={(e) => setThreadMessage(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendThreadReply(); } }}
                InputProps={{ disableUnderline: true }}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }}
              />
              {threadMessage.trim() && (
                <IconButton size="small" onClick={handleSendThreadReply} sx={{ color: accent.main }}>
                  <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* ════════════ CONTEXT MENU ════════════ */}
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 180, boxShadow: '0 8px 30px rgba(0,0,0,0.15)' } } }}
      >
        <MenuItem onClick={() => { setReplyTo(contextMenu!.message); setContextMenu(null); }}>
          <ListItemIcon><ReplyIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Reply</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { openThread(contextMenu!.message); setContextMenu(null); }}>
          <ListItemIcon><ThreadIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Reply in Thread</ListItemText>
        </MenuItem>
        {contextMenu?.message.sender.id === currentUserId && (
          <MenuItem onClick={() => { setEditingMessage(contextMenu!.message); setMessage(contextMenu!.message.content); setContextMenu(null); }}>
            <ListItemIcon><EditIcon sx={{ fontSize: 18 }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Edit</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { if (contextMenu) { navigator.clipboard.writeText(contextMenu.message.content); } setContextMenu(null); }}>
          <ListItemIcon><StarIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Copy Text</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { if (contextMenu) { contextMenu.message.isPinned ? handleUnpin(contextMenu.message.id) : handlePin(contextMenu.message.id); } setContextMenu(null); }}>
          <ListItemIcon><PinIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>{contextMenu?.message.isPinned ? 'Unpin' : 'Pin'}</ListItemText>
        </MenuItem>
        {contextMenu?.message.sender.id === currentUserId && (
          <>
            <Divider />
            <MenuItem onClick={() => { if (contextMenu) handleDeleteMsg(contextMenu.message.id); setContextMenu(null); }} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteIcon sx={{ fontSize: 18, color: 'error.main' }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* ════════════ EMOJI PICKER ════════════ */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { borderRadius: 2, p: 1.5, maxWidth: 300 } } }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, maxHeight: 200, overflowY: 'auto' }}>
          {EMOJI_LIST.map(emoji => (
            <IconButton
              key={emoji} size="small"
              onClick={() => {
                if (emojiTarget === 'input') setMessage(prev => prev + emoji);
                else handleReaction(emojiTarget as number, emoji);
                setEmojiAnchor(null);
              }}
              sx={{ fontSize: 20, p: 0.4, '&:hover': { backgroundColor: alpha(accent.main, 0.1) } }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
      </Popover>

      {/* ════════════ CREATE GROUP DIALOG ════════════ */}
      <Dialog open={channelDialogOpen} onClose={() => { setChannelDialogOpen(false); setSelectedMembers(new Set()); setMemberSearchQuery(''); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px',
              background: `linear-gradient(135deg, ${accent.main}, ${accent.dark})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 2px 8px ${alpha(accent.main, 0.3)}`,
            }}>
              <GroupsIcon sx={{ fontSize: 20, color: '#fff' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Create a Group</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus fullWidth label="Group name" variant="outlined" size="small"
            value={newChannelName} onChange={(e) => setNewChannelName(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '-'))}
            sx={{ mb: 2 }}
            placeholder="e.g. project-team"
            helperText="Use lowercase letters, numbers, and hyphens"
          />
          <TextField
            fullWidth label="Description (optional)" variant="outlined" size="small"
            value={newChannelDesc} onChange={(e) => setNewChannelDesc(e.target.value)}
            multiline rows={2}
            sx={{ mb: 2 }}
            placeholder="Brief description of the group purpose"
          />
          
          <Divider sx={{ my: 2 }} />
          
          {/* Member selection */}
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1, color: 'text.primary' }}>
              Add Members ({selectedMembers.size} selected)
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search members..."
              value={memberSearchQuery}
              onChange={(e) => setMemberSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
              }}
              sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Box sx={{ 
              maxHeight: 280, 
              overflowY: 'auto',
              border: `1px solid ${borderColor}`,
              borderRadius: 2,
              bgcolor: isDark ? alpha('#fff', 0.02) : alpha('#000', 0.01),
            }}>
              {onlineUsers.filter(u => 
                u.email.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
                getUserDisplayName(u).toLowerCase().includes(memberSearchQuery.toLowerCase())
              ).map(user => {
                const isSelected = selectedMembers.has(user.id);
                return (
                  <Box key={user.id} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    cursor: 'pointer',
                    borderBottom: `1px solid ${borderColor}`,
                    transition: 'all 0.2s',
                    bgcolor: isSelected ? alpha(accent.main, 0.08) : 'transparent',
                    '&:hover': { bgcolor: isSelected ? alpha(accent.main, 0.12) : hoverBg },
                    '&:last-child': { borderBottom: 'none' },
                  }}
                    onClick={() => {
                      const newSet = new Set(selectedMembers);
                      if (isSelected) newSet.delete(user.id);
                      else newSet.add(user.id);
                      setSelectedMembers(newSet);
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      sx={{ p: 0 }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        <Box sx={{
                          width: 10, height: 10, borderRadius: '50%',
                          bgcolor: getStatusColor(user.status),
                          border: `2px solid ${isDark ? '#1e293b' : '#fff'}`,
                        }} />
                      }
                    >
                      <Avatar
                        sx={{
                          width: 36, height: 36, fontSize: '0.75rem', fontWeight: 700,
                          background: getAvatarGradient(user.email || ''),
                        }}
                      >
                        {getInitials(user)}
                      </Avatar>
                    </Badge>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: 'text.primary' }}>
                        {getUserDisplayName(user)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {user.email}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', textTransform: 'capitalize' }}>
                      {getStatusLabel(user.status)}
                    </Typography>
                  </Box>
                );
              })}
              {onlineUsers.filter(u => 
                u.email.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
                getUserDisplayName(u).toLowerCase().includes(memberSearchQuery.toLowerCase())
              ).length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography sx={{ color: 'text.disabled', fontSize: '0.85rem' }}>No members found</Typography>
                </Box>
              )}
            </Box>
          </Box>
          
          <FormControlLabel
            control={<Switch checked={newChannelPrivate} onChange={(e) => setNewChannelPrivate(e.target.checked)} />}
            label="Private group (invite only)"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { setChannelDialogOpen(false); setSelectedMembers(new Set()); setMemberSearchQuery(''); }} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateChannel} 
            disabled={!newChannelName.trim()}
            startIcon={<GroupsIcon />}
            sx={{ 
              backgroundColor: accent.main, 
              '&:hover': { backgroundColor: accent.dark },
              px: 3,
            }}
          >
            Create Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════════════ MEMBERS DIALOG ════════════ */}
      <Dialog open={membersDialogOpen} onClose={() => setMembersDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupsIcon sx={{ fontSize: 24, color: accent.main }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Group Members</Typography>
              {activeChannel && (
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 400 }}>
                  #{activeChannel.name}
                </Typography>
              )}
            </Box>
            <Chip 
              label={`${channelMembers.length} ${channelMembers.length === 1 ? 'member' : 'members'}`}
              size="small"
              sx={{ fontWeight: 600, bgcolor: alpha(accent.main, 0.1), color: accent.main }}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingMembers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ color: accent.main }} />
            </Box>
          ) : channelMembers.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ color: 'text.disabled', fontSize: '0.9rem' }}>No members in this group</Typography>
            </Box>
          ) : (
            <Box>
              {channelMembers.map((member, idx) => (
                <Box key={member.id} sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderBottom: idx < channelMembers.length - 1 ? `1px solid ${borderColor}` : 'none',
                  transition: 'background-color 0.2s',
                  '&:hover': { bgcolor: hoverBg },
                }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      onlineUsers.find(u => u.id === member.userId) ? (
                        <Box sx={{
                          width: 12, height: 12, borderRadius: '50%',
                          bgcolor: getStatusColor(onlineUsers.find(u => u.id === member.userId)!.status),
                          border: `2px solid ${isDark ? '#1e293b' : '#fff'}`,
                        }} />
                      ) : null
                    }
                  >
                    <Avatar
                      sx={{
                        width: 42, height: 42, fontSize: '0.85rem', fontWeight: 700,
                        background: getAvatarGradient(member.user.email || ''),
                      }}
                    >
                      {member.user.firstName?.[0] || member.user.email[0].toUpperCase()}
                      {member.user.lastName?.[0] || ''}
                    </Avatar>
                  </Badge>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: 'text.primary' }}>
                        {member.user.firstName && member.user.lastName 
                          ? `${member.user.firstName} ${member.user.lastName}`.trim()
                          : member.user.email.split('@')[0]
                        }
                      </Typography>
                      {member.role === 'admin' && (
                        <Chip 
                          label="Admin" 
                          size="small" 
                          sx={{ 
                            height: 18, 
                            fontSize: '0.65rem', 
                            fontWeight: 700,
                            bgcolor: alpha(accent.main, 0.15), 
                            color: accent.main 
                          }} 
                        />
                      )}
                    </Box>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {member.user.email}
                    </Typography>
                  </Box>
                  {onlineUsers.find(u => u.id === member.userId) && (
                    <Typography sx={{ 
                      fontSize: '0.7rem', 
                      color: getStatusColor(onlineUsers.find(u => u.id === member.userId)!.status), 
                      fontWeight: 600,
                      textTransform: 'capitalize' 
                    }}>
                      {getStatusLabel(onlineUsers.find(u => u.id === member.userId)!.status)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setMembersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ════════════ POLL DIALOG ════════════ */}
      <Dialog open={pollDialogOpen} onClose={() => setPollDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PollIcon sx={{ color: accent.main }} /> Create a Poll
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Question" variant="outlined" size="small"
            value={pollQuestion} onChange={(e) => setPollQuestion(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          {pollOptions.map((opt, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth label={`Option ${i + 1}`} variant="outlined" size="small"
                value={opt} onChange={(e) => { const n = [...pollOptions]; n[i] = e.target.value; setPollOptions(n); }}
              />
              {i > 1 && (
                <IconButton size="small" onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
          ))}
          {pollOptions.length < 10 && (
            <Button size="small" startIcon={<AddIcon />} onClick={() => setPollOptions([...pollOptions, ''])} sx={{ mb: 1 }}>
              Add option
            </Button>
          )}
          <FormControlLabel
            control={<Switch checked={pollMultiSelect} onChange={(e) => setPollMultiSelect(e.target.checked)} />}
            label="Allow multiple selections"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPollDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreatePoll}
            disabled={!pollQuestion.trim() || pollOptions.filter(o => o.trim()).length < 2}
            sx={{ backgroundColor: accent.main, '&:hover': { backgroundColor: accent.dark } }}
          >
            Create Poll
          </Button>
        </DialogActions>
      </Dialog>

      {/* Typing animation keyframes */}
      <style>{`
        @keyframes typingBounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
      `}</style>
    </Box>
  );
};

export default TeamChatPage;
